import { ComponentType, lazy } from 'react';

import { Skeleton, Spinner } from '@/components/ui';

/**
 * `React.lazy` for modules that export the page as a named export:
 * `lazyNamed(() => import('@/pages/Foo'), 'FooPage')`.
 */
export const lazyNamed = <M extends Record<K, ComponentType<any>>, K extends keyof M>(
  load: () => Promise<M>,
  name: K,
) => lazy(async () => ({ default: (await load())[name] }));

const CHUNK_LOAD_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css',
  'loading chunk',
  'loading css chunk',
];

/**
 * True when a lazily imported route chunk could not be loaded, which usually means the browser still
 * runs an old build whose chunk files were replaced by a newer deploy (or the network dropped).
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  if (error.name === 'ChunkLoadError') {
    return true;
  }
  const message = error.message.toLowerCase();
  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

/**
 * Suspense fallback for a route chunk. It renders inside the page shell (header and sidebar stay
 * mounted) and fills the same content area as a loaded page, so nothing shifts when the chunk arrives.
 */
export const RouteFallback = () => (
  <div className="flex min-h-page flex-1 flex-col gap-6" data-testid="route-fallback">
    <Spinner className="size-6 text-brand" label="Loading page" />
    <div aria-hidden="true" className="flex flex-col gap-4">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  </div>
);
